import {
  createFileRoute,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import { ProductTypeEnum } from "@/requests/gen/react-query/fastAPI.schemas";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  ParamDataTable,
  dataTableSearchParsers,
  loadDataTableSearch,
} from "@/components/ui/param-data-table";
import { ColumnDef } from "@tanstack/react-table";
import { useQueryStates, type inferParserType } from "nuqs";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { keepPreviousData } from "@tanstack/react-query";
import { OwnedStockCrumbs } from "./-crumbs";
import {
  useGetOwnedStock,
  getGetOwnedStockQueryOptions,
} from "@/requests/gen/react-query/owned-stock";
import { useGetEntitiesInfinite } from "@/requests/gen/react-query/entity";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { useOnlyEntity } from "@/hooks/use-only-entity";
import formatUSD from "@/lib/format-currency";

type OwnedStockSearch = inferParserType<typeof dataTableSearchParsers>;

function ownedStockQueryParams(
  p: OwnedStockSearch,
): Parameters<typeof getGetOwnedStockQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    product_types: p.product_types,
    entity_ids: p.entity_ids,
  };
}

export const Route = createFileRoute("/_private/_stock-360/owned-stock/lookup")(
  {
    loader: ({ context: { queryClient }, location }) =>
      queryClient.prefetchQuery(
        getGetOwnedStockQueryOptions(
          ownedStockQueryParams(loadDataTableSearch(location.searchStr)),
        ),
      ),
    component: OwnedStockLookup,
  },
);

// Define the columns for the table
interface OwnedStockTableProps {
  link?: LinkProps;
  external_id: string;
  name: string;
  quantity_on_hand: number;
  value: number;
  order_in_progress: string;
  stock_percentage: number | null;
}

// Define the columns headers and accessor keys
const columns: ColumnDef<OwnedStockTableProps>[] = [
  { header: "External ID", accessorKey: "external_id" },
  { header: "Product Name", accessorKey: "name" },
  { header: "Quantity on Hand", accessorKey: "quantity_on_hand" },
  {
    header: "Value",
    accessorKey: "value",
    cell: ({ row }) => {
      return formatUSD(row.original.value);
    },
  },
  {
    header: "Order in Progress",
    accessorKey: "order_in_progress",
  },
  {
    header: "% to Reorder",
    accessorKey: "stock_percentage",
    cell: ({ row }) => {
      // If there is no Reorder level, show a badge indicating this
      if (row.original.stock_percentage === null)
        return <Badge variant="destructive">--</Badge>;

      // If there is a Reorder level, show the stock percentage
      const badgeColor =
        row.original.stock_percentage >= 100
          ? "success"
          : row.original.stock_percentage > 33
            ? "warning"
            : "destructive";
      return (
        <Badge
          variant={badgeColor}
        >{`${row.original.stock_percentage}%`}</Badge>
      );
    },
  },
];

// Main component
function OwnedStockLookup() {
  const [queryStates, setQueryStates] = useQueryStates(dataTableSearchParsers);
  const [entitySearch, setEntitySearch] = useState<string | undefined>();
  const onlyEntity = useOnlyEntity();

  // Fetch all entities
  const entities = useGetEntitiesInfinite(
    {
      search: entitySearch,
      include_ids: queryStates.entity_ids,
    },
    {
      query: {
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.items.length === 0) {
            return undefined;
          }
          return allPages.length + 1;
        },
      },
    },
  );

  const entityOptions =
    entities.data?.pages
      .flatMap((page) => page.items)
      .map((entity) => ({
        label: entity.name,
        value: entity.entity_id,
      })) || [];

  const ownedStock = useGetOwnedStock(ownedStockQueryParams(queryStates), {
    query: { placeholderData: keepPreviousData },
  });

  // Map the data to the table format
  const tableData: OwnedStockTableProps[] | undefined =
    ownedStock.data?.items.map((warehouse) => {
      // Calculate total quantity from all batches
      const totalQuantity = warehouse.warehouse_vendors.reduce(
        (total, vendor) => {
          return (
            total +
            vendor.batches.reduce((batchTotal, batch) => {
              return batchTotal + (batch.quantity_on_hand || 0);
            }, 0)
          );
        },
        0,
      );

      // Calculate total value from all batches
      const totalValue = warehouse.warehouse_vendors.reduce((total, vendor) => {
        return (
          total +
          vendor.batches.reduce((batchTotal, batch) => {
            const batchValue =
              (batch.quantity_on_hand || 0) * (batch.unit_cost || 0);
            return batchTotal + batchValue;
          }, 0)
        );
      }, 0);

      return {
        link: linkOptions({
          to: "/owned-stock/$catalogProductId",
          params: { catalogProductId: String(warehouse.warehouse_id) },
        }),
        external_id: warehouse.catalog_product.external_id || "--",
        name: warehouse.catalog_product.display_name,
        quantity_on_hand: totalQuantity,
        value: totalValue,
        order_in_progress: warehouse.warehouse_vendors[0].batches.some(
          (batch) =>
            !batch.arrived_at && batch.stock_order?.status !== "Cancelled",
        )
          ? "Yes"
          : "No",
        stock_percentage:
          warehouse.reorder_level && warehouse.reorder_level > 0
            ? Math.round((totalQuantity / warehouse.reorder_level) * 100)
            : null,
      };
    });

  return (
    <div>
      <title>Owned Stock - Legrande Health Portal</title>
      <OwnedStockCrumbs />
      <Card>
        <CardHeader>
          <CardTitle>Owned Stock</CardTitle>
          <CardDescription>
            View your owned stock below. You can search, filter, and click to
            see more details.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={ownedStock.isFetching} />
            <DropdownFilter
              title="Type"
              options={[
                {
                  items: Object.values(ProductTypeEnum).map((product_type) => {
                    return { label: product_type, value: product_type };
                  }),
                },
              ]}
              values={queryStates.product_types || []}
              onChange={(values) => setQueryStates({ product_types: values })}
            />
            {!onlyEntity && (
              <DropdownFilter
                title="Entity"
                search={entitySearch}
                fetchNextPage={entities.fetchNextPage}
                isFetching={entities.isFetchingNextPage}
                setSearch={setEntitySearch}
                options={[
                  {
                    items: entityOptions,
                  },
                ]}
                values={queryStates.entity_ids}
                onChange={(values) => setQueryStates({ entity_ids: values })}
              />
            )}
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={ownedStock.isLoading}
            isError={ownedStock.isError}
            count={ownedStock.data?.count}
          />
        </CardContent>
      </Card>
    </div>
  );
}
